import { createStyles, Paper, Typography } from '@material-ui/core';
import { debounce } from 'lodash-es';
import React from 'react';
import update from 'react-addons-update';

import { recoverPassword } from '~/src/dash/api/console-api';
import { AsyncButtonV2 } from '~/src/dash/components/button';
import { FormItemTypeText, IFormData, IFormItem, RenderForm } from '~/src/dash/components/form';
import { REGEX_EMAIL } from '~/src/dash/globals/constants';
import { isLoginDisabled } from '~/src/dash/globals/disable-login';
import { getColorTheme, getRoundLogoURL } from '~/src/dash/resellers/utils/importResellerConfig';
import { IHistoryProps, IReduxActionProps, IWithStylesProps } from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { hasErrors, IErrors, transformErrors, validatorFactory } from '~/src/dash/utils/validator';
import { BaseClass } from '~/src/dash/views/base-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps extends IWithStylesProps, IReduxActionProps, IHistoryProps {}

interface IState {
  data: IFormData;
  showLoadingWheel: boolean;
  count: number;

  errors: IErrors;
  snackbarMessage: string;
  snackbarError: boolean;
}

const forgetPasswordForm: IFormItem[] = [
  {
    id: 'Email',
    label: 'Email address',
    required: true,
    render: FormItemTypeText,
    props: {
      disabled: isLoginDisabled,
    },
  },
];

const validate = validatorFactory({
  Email(val: any): string {
    if (!val || !REGEX_EMAIL.test(val)) {
      return 'Please enter valid email.';
    }
    return '';
  },
});

export const PasswordRecoverView = decorate(
  {
    styles: (theme: any) =>
      createStyles({
        paper: {
          ...theme.mixins.gutters(),
          paddingTop: theme.spacing.unit * 2,
          paddingBottom: theme.spacing.unit * 2,
          marginTop: theme.spacing.unit * 4,
          marginRight: 'auto',
          marginLeft: 'auto',
          maxWidth: '300px',
          [theme.breakpoints.up('md')]: {
            maxWidth: '400px',
            marginTop: theme.spacing.unit * 6,
          },
        },
        signHeader: {
          marginTop: theme.spacing.unit * 4,
          textAlign: 'center',
          [theme.breakpoints.up('md')]: {
            marginTop: 0, // Margin above root component
          },
        },
        signHeaderLogo: {
          marginTop: theme.spacing.unit * 8, // Margin above logo
          display: 'inline-block',
          width: '150px',
          height: '65px',
          background: 'url(' + getRoundLogoURL() + ') no-repeat center',
          backgroundSize: 'contain',
        },
        signHeaderText: {
          color: theme.palette.secondary.contrastText,
          fontSize: theme.spacing.unit * 4,
          margin: theme.spacing.unit * 3,
        },
        signDescriptionText: {
          color: theme.palette.secondary.contrastText,
          fontSize: theme.spacing.unit * 2,
          fontWeight: 600,
          margin: 'auto',
          maxWidth: '480px',
        },
      }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      data: {} as IFormData,
      showLoadingWheel: false,
      count: 0,
      errors: {} as IErrors,
      snackbarMessage: '',
      snackbarError: false,
    };

    _recoverPassword = debounce((count: number) => {
      if (count !== this.state.count) {
        return;
      }
      recoverPassword({
        query: {
          ishb: true,
          email: this.state.data.Email,
        },
      }).then(
        (res: any) => {
          if (count !== this.state.count) {
            return;
          }
          if (res.success) {
            this.setState({
              snackbarMessage:
                'Successfully send a request to recover your password. Please check your email and follow the instructions.',
              snackbarError: false,
              showLoadingWheel: false,
            });
          } else {
            this.setState({
              snackbarMessage:
                'Recovering password failed. Please fix the error and try again later.',
              snackbarError: true,
              showLoadingWheel: false,
            });
          }
        },
        () => {
          if (count !== this.state.count) {
            return;
          }
          this.setState({
            snackbarMessage:
              'Recovering password failed. Please fix the error and try again later.',
            snackbarError: true,
            showLoadingWheel: false,
            errors: update(this.state.errors, {
              $merge: {
                Email: "We can't find this email in our database.",
              },
            }),
          });
        },
      );
    }, 200);

    constructor(props: IProps) {
      super(props);
    }

    recoverPassword = () => {
      if (isLoginDisabled) {
        return;
      }

      if (!this.validateForm()) {
        this.setState({
          snackbarMessage: 'Recovering password failed. Please fix the error and try again later.',
          snackbarError: true,
        });
        return;
      }
      const count = this.state.count + 1;
      this.setState({
        showLoadingWheel: true,
        count,
      });
      this._recoverPassword(count);
    };

    onUpdate = (data: IFormData) => {
      if (hasErrors(this.state.errors)) {
        this.validateForm();
      }
      this.setState({ data });
    };

    handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      this.recoverPassword();
    };

    validateForm = (): boolean => {
      const errors = validate(this.state.data);
      if (hasErrors(errors)) {
        this.setState({
          errors,
        });
        return false;
      }
      this.setState({
        errors: {} as IErrors,
      });
      return true;
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    render = () => {
      const { classes } = this.props;
      return (
        <div
          style={{
            width: '100%',
            height: '100vh',
            backgroundColor: getColorTheme('signInBackground'),
            margin: '0px 0px 0px 0px',
          }}
        >
          <BaseClass id='signin'>
            <div className={classes.signHeader}>
              <div className={classes.signHeaderLogo} />
              <h3 className={classes.signHeaderText}>Reset password</h3>
              <h5 className={classes.signDescriptionText}>
                Enter the email address associated with your account, and we'll email you a link to
                reset your password.
              </h5>
            </div>
            <Paper
              classes={{
                root: classes.paper,
              }}
            >
              <form id='signin-form-body' onSubmit={this.handleSubmit}>
                <RenderForm
                  schema={forgetPasswordForm}
                  data={this.state.data}
                  errors={transformErrors(this.state.errors || {})}
                  onUpdate={this.onUpdate}
                />
                <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                  <div>
                    {isLoginDisabled && (
                      <Typography
                        component='p'
                        className='error-message'
                        style={{ marginBottom: '1rem' }}
                      >
                        Password recovery is temporarily disabled.
                      </Typography>
                    )}
                  </div>

                  <AsyncButtonV2
                    text='Send reset link'
                    inprogress={this.state.showLoadingWheel}
                    props={{
                      size: 'large',
                    }}
                    onClick={this.recoverPassword}
                    disabled={isLoginDisabled}
                  />
                </div>
              </form>
            </Paper>
            <SnackbarView
              open={!!this.state.snackbarMessage}
              error={!!this.state.snackbarError}
              onClose={this.snackbarClose}
            >
              {this.state.snackbarMessage}
            </SnackbarView>
          </BaseClass>
        </div>
      );
    };
  },
);
